import { FC } from "react";

import {
  EventBusTopic,
  FeedbackAvoModuleName,
} from "@moodys/mdc-frontend.utils.event-bus";
import {
  Divider,
  List,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { deepmerge } from "@mui/utils";

import { MoodysLogo, MoodysThemeProvider } from "@moodys/federated";
import { useFooterTranslation } from "components/Footer/i18n";
import { useLayoutContext } from "components/Layout";
import { useEventBus } from "hooks/useEventBus";
import { generateThemeOverrides, getLegacyTypography } from "theme";
import { getHomepageLinkForLanguage, getPageLanguage } from "utils/language";

import { footerStyles as styles } from "./Footer.styles";
import { ExpansionNode } from "./components/ExpansionNode";
import { LinkGroup } from "./components/LinkGroup";
import { SocialLinks } from "./components/SocialLinks";
import { FooterContextProvider, useClickOneTrustButton } from "./context";
import { footerModel } from "./model";

interface FooterProps {
  isLegacy?: boolean;
}

export const Footer: FC<FooterProps> = ({ isLegacy = false }) => {
  const { themeMode, rootFontSize } = useLayoutContext();

  const baseThemeOverrides = generateThemeOverrides({
    themeMode,
  });
  const themeOverrides = isLegacy
    ? deepmerge(baseThemeOverrides, getLegacyTypography({ rootFontSize }))
    : baseThemeOverrides;

  return (
    <MoodysThemeProvider
      colorPalette={themeMode === "dark" ? "mdcBlue" : "default"}
      themeOverrides={themeOverrides}
      responsiveFontSizes={!isLegacy}
    >
      <FooterContextProvider>
        <FooterWithContext />
      </FooterContextProvider>
    </MoodysThemeProvider>
  );
};

export const FooterWithContext = () => {
  const [languageNodes] = useEventBus(
    EventBusTopic.Shell.AemMenu.LanguageNodes
  );
  const [, setFeedbackState] = useEventBus(EventBusTopic.Forms.FeedbackState);
  const [isAuthenticated] = useEventBus(EventBusTopic.IsAuthenticated);

  const { translate } = useFooterTranslation();
  const isSmall = useMediaQuery(useTheme().breakpoints.down("md"));
  const { clickOneTrustButton } = useClickOneTrustButton();

  const pageLanguage = getPageLanguage(languageNodes);
  const homeLink = getHomepageLinkForLanguage(languageNodes);

  const year = new Date().getFullYear();

  const handleFeedbackFormOpen = (modalOpen: boolean) => {
    setFeedbackState({
      avoModuleName: FeedbackAvoModuleName.Footer,
      modalOpen,
    });
  };

  return (
    <footer
      css={styles.footer}
      data-testid="web-core-footer"
      id="web-core-footer"
    >
      <div css={styles.container}>
        <a aria-label="Moody's" href={homeLink}>
          <MoodysLogo
            color="white"
            data-testid="footer-Moodys-logo"
            height="32px"
          />
        </a>
        {isSmall ? (
          <List css={styles.list}>
            {footerModel.map((node) => (
              <ExpansionNode
                key={node.titleKey}
                node={node}
                clickOneTrustButton={clickOneTrustButton}
                pageLanguage={pageLanguage}
                setFeedbackFormOpen={handleFeedbackFormOpen}
                translate={translate}
                isAuthenticated={isAuthenticated}
              />
            ))}
          </List>
        ) : (
          <div css={styles.desktopContainerLinks}>
            {footerModel.map((node) => (
              <LinkGroup
                key={node.titleKey}
                node={node}
                clickOneTrustButton={clickOneTrustButton}
                pageLanguage={pageLanguage}
                setFeedbackFormOpen={handleFeedbackFormOpen}
                translate={translate}
                isAuthenticated={isAuthenticated}
              />
            ))}
          </div>
        )}
        <Divider css={styles.endingDivider} />
        <Stack
          spacing={2}
          direction={{ xs: "column", md: "row" }}
          sx={{
            alignItems: { md: "center" },
          }}
        >
          <SocialLinks />
          <Stack
            spacing={2}
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{
              flex: 1,
              justifyContent: "space-between",
            }}
          >
            <Typography
              data-testid="social-disclaimer"
              variant="caption"
              align={isSmall ? "left" : "right"}
              maxWidth={{ md: "50%" }}
              css={styles.caption}
            >
              {translate("socialDisclaimer")}
            </Typography>
            <Typography
              data-testid="copyright"
              variant="caption"
              align={isSmall ? "left" : "right"}
              maxWidth={{ md: "50%", lg: "none" }}
              css={styles.caption}
            >
              &copy; {year} {translate("copyright")}
            </Typography>
          </Stack>
        </Stack>
      </div>
      <script
        async
        src="https://bugcrowd.com/d677c912-8042-4abd-863f-e34cb95292f7/external/script"
        data-bugcrowd-program="https://bugcrowd.com/d677c912-8042-4abd-863f-e34cb95292f7/external/report"
        data-testid="bugcrowd-script"
      ></script>
    </footer>
  );
};
